/**
 * QR Code Renderer
 * Generates QR codes on Canvas for card exports
 */
import QRCode from 'qrcode';

export interface QRCodeOptions {
  size: number;
  darkColor?: string;      // Default: '#000000'
  lightColor?: string;     // Default: transparent
  margin?: number;         // Default: 0
  errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H';
}

/**
 * Generate a QR code as a Canvas element
 */
export async function generateQRCodeCanvas(
  text: string,
  options: QRCodeOptions
): Promise<HTMLCanvasElement> {
  const canvas = document.createElement('canvas');
  
  await QRCode.toCanvas(canvas, text, {
    width: options.size,
    margin: options.margin ?? 0,
    errorCorrectionLevel: options.errorCorrectionLevel ?? 'M',
    color: {
      dark: options.darkColor ?? '#000000',
      light: options.lightColor ?? '#00000000', // Transparent
    },
  });
  
  return canvas;
}

/**
 * Generate a QR code as a data URL
 */
export async function generateQRCodeDataURL(
  text: string,
  options: QRCodeOptions
): Promise<string> {
  const dataUrl = await QRCode.toDataURL(text, {
    width: options.size,
    margin: options.margin ?? 0,
    errorCorrectionLevel: options.errorCorrectionLevel ?? 'M',
    color: {
      dark: options.darkColor ?? '#000000',
      light: options.lightColor ?? '#00000000', // Transparent
    },
  });
  
  return dataUrl;
}

/**
 * Draw a QR code directly onto an existing canvas context
 */
export async function drawQRCodeOnCanvas(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  width: number,
  height: number,
  options?: Partial<QRCodeOptions>
): Promise<void> {
  // Generate QR at a higher resolution for quality
  const qrSize = Math.max(width, height) * 2;
  
  const qrCanvas = await generateQRCodeCanvas(text, {
    size: qrSize,
    darkColor: options?.darkColor ?? '#000000',
    lightColor: options?.lightColor ?? '#00000000',
    margin: options?.margin ?? 0,
    errorCorrectionLevel: options?.errorCorrectionLevel ?? 'M',
  });
  
  // Draw the QR code scaled to fit the specified dimensions
  ctx.drawImage(qrCanvas, x, y, width, height);
}
